import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { collectCanvasItems, type CanvasItem } from './collect-items';
import { baselineForScope, planAutoOpen, type CanvasBaseline } from './auto-open';
import {
  DEFAULT_CANVAS_VIEW_STATE,
  loadCanvasViewState,
  reconcileViewState,
  resolveActiveKey,
  saveCanvasViewState,
  type CanvasView,
  type CanvasViewState,
} from './canvas-storage';
import { canvasLayout, type CanvasLayout } from './canvas-layout';
import type { CanvasContextValue } from './canvas-context';
import type { StoredViewState } from './canvas-storage';

export interface CanvasState {
  layout: CanvasLayout;
  items: CanvasItem[];
  open: boolean;
  activeKey: string | null;
  view: CanvasView;
  /** Narrow screens: something changed while the person was on the Chat view. */
  unseen: boolean;
  /** Panel open: something changed but was NOT activated (an unsaved draft is on screen). */
  panelUnseen: boolean;
  setOpen: (open: boolean) => void;
  setView: (view: CanvasView) => void;
  select: (key: string) => void;
  context: CanvasContextValue | null;
}

/**
 * FRONT-2594 — everything the chat window needs to run Canvas: the item list
 * derived from the messages, the persisted per-chat view state, and auto-open.
 *
 * `historyReady` must be true only once the chat's history has loaded AND been
 * seeded into `messages`; until then the diff baseline is not adopted, so
 * history arriving late is never mistaken for something Eddy just made.
 *
 * `branchId` scopes the baseline together with the chat: a branch switch swaps
 * the whole message list, so each (chat, branch) adopts its own baseline once
 * that branch's history is seeded. `lockActive` (an unsaved document draft is on
 * screen) turns an auto-open into a mark only, so typing is never pulled away.
 *
 * Flag off (`enabled` false): no items, no layout, and nothing is ever written
 * to storage.
 */
export function useCanvasState(input: {
  sessionId: string;
  enabled: boolean;
  messages: readonly { parts?: unknown[] }[];
  branchId: string;
  historyReady: boolean;
  wide: boolean;
  lockActive: boolean;
}): CanvasState {
  const { sessionId, enabled, messages, branchId, historyReady, wide, lockActive } = input;
  const items = useMemo(() => (enabled ? collectCanvasItems(messages) : []), [enabled, messages]);

  const [stored, setStored] = useState<StoredViewState>(() => reconcileViewState(
    { sid: sessionId, loaded: false, state: DEFAULT_CANVAS_VIEW_STATE },
    sessionId,
    enabled,
  ));
  // The modal can swap onto another thread in place, and the feature flag
  // usually resolves after mount: reload that chat's state during render (not in
  // an effect) so one frame never shows the old chat's panel or the default.
  const reconciled = reconcileViewState(stored, sessionId, enabled);
  if (reconciled !== stored) setStored(reconciled);
  const state = reconciled.state;

  const sidRef = useRef(sessionId);
  sidRef.current = sessionId;
  const update = useCallback(
    (patch: Partial<CanvasViewState>) => {
      setStored((prev) => {
        const next = { ...prev.state, ...patch };
        if (enabled) saveCanvasViewState(sidRef.current, next);
        return { sid: sidRef.current, loaded: prev.loaded, state: next };
      });
    },
    [enabled],
  );

  const activeKey = resolveActiveKey(state.activeKey, items.map((i) => i.key));
  const layout = canvasLayout({ enabled, itemCount: items.length, open: state.open, wide });

  // Auto-open ---------------------------------------------------------------
  const baselineRef = useRef<{ scope: string; baseline: CanvasBaseline | null }>({
    scope: `${sessionId}\u0000${branchId}`,
    baseline: null,
  });
  const wideRef = useRef(wide);
  wideRef.current = wide;
  const lockRef = useRef(lockActive);
  lockRef.current = lockActive;
  const openRef = useRef(state.open);
  openRef.current = state.open;
  const [unseen, setUnseen] = useState(false);
  const [panelUnseen, setPanelUnseen] = useState(false);
  useEffect(() => {
    const kept = baselineForScope(baselineRef.current, `${sessionId}\u0000${branchId}`);
    baselineRef.current = { scope: kept.scope, baseline: kept.baseline };
    if (kept.reset) {
      setUnseen(false);
      setPanelUnseen(false);
    }
    if (!enabled) return;
    const plan = planAutoOpen(kept.baseline, items, historyReady, wideRef.current, lockRef.current);
    baselineRef.current = { scope: kept.scope, baseline: plan.baseline };
    if (plan.action.kind === 'open') update({ open: true, activeKey: plan.action.key });
    else if (plan.action.kind === 'unseen') {
      if (wideRef.current && openRef.current) setPanelUnseen(true);
      else setUnseen(true);
    }
  }, [sessionId, branchId, enabled, historyReady, items, update]);

  useEffect(() => {
    if (state.open && unseen) setUnseen(false);
    if (!state.open && panelUnseen) setPanelUnseen(false);
  }, [state.open, unseen, panelUnseen]);

  const setOpen = useCallback((open: boolean) => update({ open }), [update]);
  const setView = useCallback((view: CanvasView) => update({ view }), [update]);
  const select = useCallback(
    (key: string) => {
      setPanelUnseen(false);
      update({ activeKey: key });
    },
    [update],
  );
  const openItem = useCallback((key: string) => update({ open: true, activeKey: key }), [update]);

  const context = useMemo<CanvasContextValue | null>(
    () =>
      enabled && items.length > 0
        ? { items, open: state.open, activeKey, view: state.view, openItem }
        : null,
    [enabled, items, state.open, activeKey, state.view, openItem],
  );

  return {
    layout,
    items,
    open: state.open,
    activeKey,
    view: state.view,
    unseen: unseen && !state.open,
    panelUnseen: panelUnseen && state.open,
    setOpen,
    setView,
    select,
    context,
  };
}
